import {Button} from '@dropbox/dig-components/buttons';
import {Spinner} from '@dropbox/dig-components/progress_indicators';
import {Text} from '@dropbox/dig-components/typography';
import {Box, Split, Stack} from '@dropbox/dig-foundations';
import {UIIcon} from '@dropbox/dig-icons';
import {ArrowRightLine} from '@dropbox/dig-icons/assets';
import type {AiSearchAnswerStartedBy} from 'analytics/aiSearchAnalytics';
import type {LiveAnswerPlacement} from 'analytics/liveAnswerAnalytics';
import {AiQueryConversationTurn, emptyAssistant} from 'components/ai/aiQueryConversation';
import {CompanyAskGlyph} from 'components/DSYS/CompanyOSLogo';
import {t} from 'i18next';
import type {MouseEvent} from 'react';

import {useLiveAnswer} from './useLiveAnswer';

/**
 * A step without the question it quotes ("Searching guides for “…”" →
 * "Searching guides"): the question is already the panel's title.
 */
const shortStep = (step: string) => step.replace(/\s+for\s+“[^”]*”/, '');

const copy = (text: string) => void navigator.clipboard.writeText(text);

/**
 * The panel's answer box: a real Ask turn for `question`, filled in as it
 * comes back: one quiet line for the latest step until text arrives, then
 * just the whole answer, formatted the way the drawer formats it (no step
 * lines). "Continue in Ask" picks the same turn
 * up in the drawer. `startedBy` is why it's there: the box led with it while
 * typing, or the person picked it.
 */
export const LiveAnswer = ({
  question,
  label,
  placement = 'pane',
  startedBy = 'typing',
}: {
  question: string;
  label?: string;
  placement?: LiveAnswerPlacement;
  startedBy?: AiSearchAnswerStartedBy;
}) => {
  const {progress, open, linkClicked} = useLiveAnswer(question, placement, startedBy);
  const answer = progress?.answer;

  return (
    <Box
      padding="12"
      borderRadius="Large"
      backgroundColor="Background Subtle"
      onClickCapture={(event: MouseEvent) => {
        // A source opened from the answer: logged as the kind of page, never the link.
        const href = (event.target as HTMLElement).closest('a[href]')?.getAttribute('href');
        if (href) linkClicked(href);
      }}
    >
      <Stack gap="8">
        <Split gap="8" alignY="center">
          <Split.Item>
            <UIIcon src={CompanyAskGlyph} />
          </Split.Item>
          <Split.Item width="fill" style={{minWidth: 0}}>
            <Text size="small" color="subtle" tagName="div">
              {label ?? question}
            </Text>
          </Split.Item>
        </Split>
        {answer ? (
          <AiQueryConversationTurn
            turn={{
              id: question,
              // No bubble: the question is already the pane's title or the field.
              question: '',
              assistant: {
                ...emptyAssistant(!progress.done),
                answer,
                // The steps say what it's doing; once it's answering they're noise.
                trace: [],
              },
            }}
            onCopy={copy}
          />
        ) : (
          <Split gap="8" alignY="center">
            <Split.Item style={{lineHeight: 0}}>
              <Spinner size="small" monochromatic />
            </Split.Item>
            <Split.Item width="fill" style={{minWidth: 0}}>
              <Text color="subtle" tagName="div">
                {progress?.step ? shortStep(progress.step) : t('search_box_answer_starting')}
              </Text>
            </Split.Item>
          </Split>
        )}
        <Box>
          <Button
            variant="opacity"
            size="small"
            withIconEnd={<UIIcon src={ArrowRightLine} />}
            onClick={open}
          >
            {t('search_box_continue_in_ask')}
          </Button>
        </Box>
      </Stack>
    </Box>
  );
};
